import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Loader2 } from 'lucide-react';
import BookedToursCard from '../components/tenant/BookedToursCard';
import CancelTourDialog from '../components/tenant/CancelTourDialog';
import TourDateTimePicker from '../components/tenant/TourDateTimePicker';
import type { SchedulingConflictSubmit } from '../components/tenant/SchedulingConflictDialog';
import { listBookedTours, type BookedTour } from '../utils/tour-home';
import { tourBookingProgress } from '../utils/prospect-sidebar-progress';
import { useSetPageProgress } from '../contexts/ProspectPageProgressContext';
import { useTenantApiToken } from '../contexts/TenantProfileContext';
import {
  bookProspectTour,
  cancelProspectTour,
  createProspectSchedulingConflict,
  getProspectTours,
  textProspectTourCalendar,
  TourBookingRejectedError,
  type ProspectTourBookingPage,
} from '../services/tenantApi';
import { trackTenantGoogleAdsOnce } from '../utils/googleAdsClick';
import { GOOGLE_ADS_EVENTS, GOOGLE_ADS_ONCE_KEYS } from '@shared/utils/google-ads';

const BOOK_TOURS_SHELL = 'mx-auto w-full max-w-lg md:max-w-3xl lg:max-w-none';
const SECTION_CARD =
  'rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 px-4 py-4 shadow-bento sm:px-6 sm:py-5';

export default function BookToursPage(): ReactElement {
  const getToken = useTenantApiToken();
  const setPageProgress = useSetPageProgress();
  const [page, setPage] = useState<ProspectTourBookingPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookingNotice, setBookingNotice] = useState<string | null>(null);
  const [bookingNoticeKind, setBookingNoticeKind] = useState<'success' | 'alert'>('alert');
  const [cancelTarget, setCancelTarget] = useState<BookedTour | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const next = await getProspectTours(getToken);
      setPage(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load tour times.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!page) return;
    const bookingIds = [
      ...listBookedTours(page.windows).map((tour) => tour.bookingId),
      ...page.completedTours.map((tour) => tour.bookingId),
    ];
    setPageProgress('/book-tours', tourBookingProgress(bookingIds));
  }, [page, setPageProgress]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  const confirmTour = async (windowId: string, startAt: string) => {
    if (busy || !page) return;
    if (!page.discoveryCallShowed) {
      setBookingNoticeKind('alert');
      setBookingNotice('Discovery call must be completed prior to booking.');
      return;
    }
    const window = page.windows.find((item) => item.id === windowId);
    const slot = window?.slots.find(
      (item) =>
        item.startAt === startAt &&
        (item.state === 'open' || item.state === 'discovery_required'),
    );
    if (!window || !slot) return;
    setBusy(true);
    setError(null);
    setBookingNotice(null);
    setBookingNoticeKind('alert');
    try {
      const next = await bookProspectTour(getToken, window.id, slot.startAt);
      setPage(next);
      setBookingNoticeKind('success');
      setBookingNotice('Tour booked');
      trackTenantGoogleAdsOnce(GOOGLE_ADS_ONCE_KEYS.TOUR_BOOKED, GOOGLE_ADS_EVENTS.TOUR_BOOKED);
    } catch (err) {
      if (err instanceof TourBookingRejectedError) {
        if (err.page) setPage(err.page);
        else await load();
        setBookingNoticeKind('alert');
        setBookingNotice(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Unable to book that tour.');
        await load();
      }
    } finally {
      setBusy(false);
    }
  };

  const submitConflict = async (input: SchedulingConflictSubmit) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setBookingNotice(null);
    try {
      await createProspectSchedulingConflict(getToken, {
        ...(input.building && input.propertyId
          ? { propertyId: input.propertyId }
          : { unitId: input.unitId }),
        reason: input.reason,
        reasonNote: input.reasonNote,
        suggestions: input.suggestions,
      });
      setBookingNoticeKind('success');
      setBookingNotice(
        "We've saved the times that could work for you. Our team will be arranging additional tour times for this property.",
      );
    } finally {
      setBusy(false);
    }
  };

  const requestCancel = (bookingId: string) => {
    if (busy || !page) return;
    const tour = listBookedTours(page.windows).find((item) => item.bookingId === bookingId);
    if (!tour) return;
    setCancelError(null);
    setCancelTarget(tour);
  };

  const textCalendar = async (bookingIds: string[], audience: 'self' | 'group' = 'group') => {
    await textProspectTourCalendar(getToken, bookingIds, audience);
  };

  const nothingOnTheShortlist =
    page != null &&
    (page.homes ?? []).length === 0 &&
    page.windows.length === 0 &&
    page.completedTours.length === 0 &&
    page.cancelledTours.length === 0;

  const confirmCancel = async (note: string) => {
    if (busy || !cancelTarget) return;
    setBusy(true);
    setCancelError(null);
    setBookingNotice(null);
    try {
      const next = await cancelProspectTour(getToken, cancelTarget.bookingId, note);
      setPage(next);
      setCancelTarget(null);
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Unable to cancel that tour.');
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`${BOOK_TOURS_SHELL} flex flex-col gap-4 pb-[max(6rem,calc(env(safe-area-inset-bottom)+5rem))]`}
    >
      <header className={`${SECTION_CARD} shrink-0`}>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Step 4</p>
        <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">Book Tours</h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400 sm:text-base">
          Choose a shortlisted home, pick a day that already has a tour, then choose a 15-minute time.
        </p>
      </header>

      {error ? (
        <p className="rounded-2xl border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/30 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">{error}</p>
      ) : null}

      {loading ? (
        <p className={`flex items-center gap-2 text-sm text-gray-500 ${SECTION_CARD}`}>
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading tour times…
        </p>
      ) : null}

      {!loading && page && nothingOnTheShortlist ? (
        <div className="flex min-h-[calc(100dvh-18rem)] items-center justify-center px-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 px-6 py-8 text-center shadow-bento">
            <p className="text-base font-medium text-gray-900 dark:text-white">
              Check back later for more options as they become available.
            </p>
          </div>
        </div>
      ) : null}

      {!loading && page && !nothingOnTheShortlist ? (
        <div className="flex flex-col">
          {(page.homes ?? []).length > 0 || page.windows.length > 0 ? (
            <TourDateTimePicker
              homes={page.homes ?? []}
              windows={page.windows}
              discoveryCallShowed={page.discoveryCallShowed}
              busy={busy}
              notice={bookingNotice}
              noticeKind={bookingNoticeKind}
              onBook={(windowId, startAt) => {
                void confirmTour(windowId, startAt);
              }}
              onCancel={requestCancel}
              onSubmitConflict={submitConflict}
            >
              <BookedToursCard
                windows={page.windows}
                completedTours={page.completedTours}
                cancelledTours={page.cancelledTours}
                groupName={page.groupName}
                calendarTextRecipients={page.calendarTextRecipients}
                busy={busy}
                onCancel={requestCancel}
                onTextCalendar={textCalendar}
              />
            </TourDateTimePicker>
          ) : (
            <BookedToursCard
              windows={page.windows}
              completedTours={page.completedTours}
              cancelledTours={page.cancelledTours}
              groupName={page.groupName}
              calendarTextRecipients={page.calendarTextRecipients}
              busy={busy}
              onCancel={requestCancel}
              onTextCalendar={textCalendar}
            />
          )}
          {cancelTarget ? (
            <CancelTourDialog
              tour={cancelTarget}
              busy={busy}
              error={cancelError}
              onClose={() => {
                if (busy) return;
                setCancelTarget(null);
                setCancelError(null);
              }}
              onConfirm={(note) => {
                void confirmCancel(note);
              }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
